import { useState, type CSSProperties } from 'react';
import { format } from 'date-fns';
import { motion, useReducedMotion } from 'motion/react';

import { sidebarPanelSpring } from '@/shared/constants/motion.constants';

/**
 * The chrome every detail panel in this library wears: the box that opens by width, the field
 * groups inside it, and the edge you drag to widen it.
 *
 * Shared because an asset and a font sit in the same column and open the same way, and a person
 * switching between the two should not feel two different panels. What differs between them is only
 * what is inside, which each panel supplies.
 */

/**
 * Wide enough at rest that the tag editor opens in its two columns rather than stacked.
 *
 * The panel used to open at 360, which left 320 inside the body's padding: under the width two
 * columns of chips and a value field need, so the layout a person sees by default was the fallback
 * one. Dragging narrower still stacks them; this only moves where the panel starts.
 */
const DEFAULT_WIDTH = 440;
export const PANEL_MIN_WIDTH = 280;
const MAX_WIDTH = 640;

/**
 * The space between the library and this panel, owned here rather than by the row's `gap`.
 *
 * A gap on the parent would hold its full width while the panel animates through zero, so the
 * library would stop short of an invisible panel and then jump. Animated alongside the width, the
 * space closes in step.
 *
 * 8px is `--space-app-inset`, the same step the page puts between its header and this row, so the
 * three surfaces are spaced alike whichever direction you read them in. It cannot be the token
 * itself: this is a number `motion` animates, not a class.
 */
const PANEL_GAP = 8;

/**
 * A UTC-anchored date, formatted for reading rather than for a machine.
 *
 * Built from `getUTCFullYear`/`getUTCMonth`/`getUTCDate` rather than the local equivalents: a
 * timestamp near midnight UTC would otherwise read as a different day depending on which
 * timezone the browser (or CI runner) is in. Reconstructing a local `Date` from the UTC parts and
 * formatting that keeps the displayed day stable everywhere.
 *
 * Total rather than throwing: the dev API a frontend build commonly runs against predates the
 * fields these panels read, so a missing or unparseable value is a normal condition, not an edge
 * case, and the type saying `string` does not mean the wire always agrees.
 */
export const formatRecordedDate = (
  value: string | null | undefined,
  notRecorded: string,
): string => {
  if (value == null) return notRecorded;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return notRecorded;
  const anchored = new Date(parsed.getUTCFullYear(), parsed.getUTCMonth(), parsed.getUTCDate());
  // eslint-disable-next-line i18next/no-literal-string -- a date-fns format pattern, not copy
  return format(anchored, 'd MMM yyyy');
};

/**
 * One labelled group of fields, always open.
 *
 * These were collapsible, and the toggles cost more than they saved: the four groups an asset
 * carries come to about a dozen short rows, so folding them away hides a screenful that already
 * fits while asking a person to remember which of the four they last left shut. A collapse earns
 * its place again when a group can hold an unbounded list.
 *
 * No rule between groups either. Four of them down one narrow column turned the panel into a stack
 * of boxes, and the heading over each already says where one ends; the space the body puts between
 * them is what does the separating.
 */
export const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <section className="flex flex-col gap-3">
    <h3 className="text-sm font-medium text-foreground">{title}</h3>
    {children}
  </section>
);

/**
 * One labelled value, label left and value right.
 *
 * `wrap` is for the ones that are prose rather than a word: a licence paragraph pushed into the
 * right-hand column would set itself one word per line, so those give up the two-column read and
 * take the full width instead.
 */
export const Field = ({
  label,
  value,
  testId,
  wrap = false,
}: {
  label: string;
  value: string;
  testId?: string;
  wrap?: boolean;
}) =>
  wrap ? (
    <div className="flex flex-col gap-1 text-sm">
      <dt className="text-muted-foreground">{label}</dt>
      <dd data-testid={testId} className="whitespace-pre-line break-words text-foreground">
        {value}
      </dd>
    </div>
  ) : (
    <div className="flex items-baseline justify-between gap-3 text-sm">
      <dt className="shrink-0 text-muted-foreground">{label}</dt>
      <dd data-testid={testId} className="min-w-0 break-words text-right text-foreground">
        {value}
      </dd>
    </div>
  );

/**
 * The panel's own width, and the drag that changes it.
 *
 * Held per panel rather than shared: closing one and opening the other is a new panel, and
 * inheriting the width somebody dragged for a font would be a surprise on an asset.
 */
export const useResizablePanel = () => {
  const [width, setWidth] = useState(DEFAULT_WIDTH);

  const startResize = (event: React.PointerEvent<HTMLDivElement>) => {
    const startX = event.clientX;
    const startWidth = width;

    const handleMove = (moveEvent: PointerEvent) => {
      const next = startWidth + (startX - moveEvent.clientX);
      setWidth(Math.min(MAX_WIDTH, Math.max(PANEL_MIN_WIDTH, next)));
    };

    const handleEnd = () => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleEnd);
    };

    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', handleEnd);
  };

  return { width, startResize };
};

/**
 * The panel's box, which opens and closes by changing width so the library beside it reflows in
 * step rather than being covered.
 *
 * Width is animated through a unitless multiplier rather than directly: the pixel width is
 * something a person drags, and springing towards each new value would make the panel lag the
 * pointer. `--panel-width` is written straight from state, and only `--panel-reveal` moves.
 *
 * The contents keep the panel's minimum width and are clipped by the box, so closing reveals and
 * conceals them instead of squeezing the header's two buttons together on the way out. No fade
 * alongside it: the clip already hides what is leaving, and fading the panel out would also fade
 * the picture it is still showing on the way.
 */
export const PanelShell = ({
  width,
  testId,
  children,
}: {
  width: number;
  testId: string;
  children: React.ReactNode;
}) => {
  const shouldReduceMotion = useReducedMotion();

  const body = (
    <div style={{ minWidth: PANEL_MIN_WIDTH }} className="flex min-h-0 flex-1 flex-col">
      {children}
    </div>
  );

  if (shouldReduceMotion) {
    return (
      <aside
        data-testid={testId}
        style={{ width, marginLeft: PANEL_GAP }}
        className="relative flex shrink-0 flex-col overflow-hidden rounded-lg bg-card"
      >
        {body}
      </aside>
    );
  }

  return (
    <motion.aside
      data-testid={testId}
      style={
        {
          '--panel-width': `${String(width)}px`,
          width: 'calc(var(--panel-width) * var(--panel-reveal))',
        } as CSSProperties
      }
      initial={{ '--panel-reveal': 0, marginLeft: 0 }}
      animate={{ '--panel-reveal': 1, marginLeft: PANEL_GAP }}
      exit={{ '--panel-reveal': 0, marginLeft: 0 }}
      transition={sidebarPanelSpring}
      className="relative flex shrink-0 flex-col overflow-hidden rounded-lg bg-card"
    >
      {body}
    </motion.aside>
  );
};
